import { useAiActionStore } from "@/store/zustand/aiActionStore";
import useDesignSystemStore, {
  GenerationStatus,
} from "@/store/zustand/aiDesignSystemBuilder";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { generateJsonStream } from "@/utils/ai/utils";
import { track } from "@/utils/events/tracker";
import {
  fixJson,
  getUnexpiredToken,
  logAxiosError,
  useCanvasId,
} from "@/utils/utils";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { useSession } from "@clerk/clerk-react";
const useGenerateOverview = () => {
  const canvasId = useCanvasId();

  const generation = useDesignSystemStore((state) => state.generation);
  const examples = generation.context.examples;
  const selectedFiles = generation.context.selectedFiles;

  const { session } = useSession();
  const setName = useDesignSystemStore((state) => state.setName);
  const setStories = useDesignSystemStore((state) => state.setStories);
  const setProps = useDesignSystemStore((state) => state.setProps);
  const setComponentOverview = useDesignSystemStore(
    (state) => state.setComponentOverview
  );
  const setNotes = useDesignSystemStore((state) => state.setNotes);
  const setStatus = useDesignSystemStore((state) => state.setStatus);

  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  const updateStoreWithJson = (json: any) => {
    if (json.story_name) {
      setName(json.story_name);
    }

    if (json.variants) {
      setStories(json.variants);
    }

    if (json.props) {
      setProps(json.props);
    }

    if (json.component_overview) {
      setComponentOverview(json.component_overview);
    }

    if (json.notes) {
      setNotes(json.notes);
    }
  };

  const generateOverview = useCallback(() => {
    const request = async () => {
      setStatus(GenerationStatus.GeneratingContext);

      track("AI_PROMPT_REQUESTED", { type: "DESIGN_SYSTEM_GENERATE_OVERVIEW" });

      try {
        const url = `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/ai/vercel-ai/aiCreateStoryOverview`;
        const body = {
          examples: JSON.stringify(examples),
          filenames: selectedFiles.map((file: any) => file.fullPath),
        };

        const supabaseJWT = await getUnexpiredToken(session);

        const stream = await generateJsonStream(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        let res = null;
        let chunkedStory = "";

        for await (let chunk of stream) {
          if (chunk.status === -1 && chunk.message === "intermediate") {
            const parsedChunk = JSON.parse(chunk.data);

            if (parsedChunk.type === "text-delta") {
              chunkedStory += parsedChunk.chunk;
              updateStoreWithJson(fixJson(chunkedStory).fixedObj);
            }
          } else {
            res = chunk;
            break;
          }
        }

        // @ts-expect-error TS18047
        if (res.status >= 400) {
          console.error({
            // @ts-expect-error TS18047
            msg: res.message,
            // @ts-expect-error TS18047
            internalErrorStr: res.internalErrorStr,
          });
          // @ts-expect-error TS18047
          throw new Error(res.message);
        }

        // @ts-expect-error TS18047
        updateStoreWithJson(JSON.parse(res.data.steps[0].text));
        setStatus(GenerationStatus.Editing);
        toast.success("Generated overview");
      } catch (err) {
        logAxiosError("Error generating story overview", err);
        toast.error(`Error generating story overview: ${err.message}`);
        setStatus(GenerationStatus.GeneratingContext);
      } finally {
        refreshPlanInfo(session);
      }
    };

    request();
  }, [
    canvasId,
    examples,
    selectedFiles,
    Boolean(session),
    updateStoreWithJson,
  ]);

  return {
    generateOverview,
  };
};

export default useGenerateOverview;
